import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    OutlinedInput,
    InputAdornment,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { capitalizeFirstLetter, validateInputValue } from 'utils/commonHelpers';
import ApiHelper from 'utils/apiHelper';

const AddScheduledEventDialog = ({ onClose, scriptStatus }) => {
    const [days, setDays] = useState({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false
    });
    const [eventType, setEventType] = useState('wake');
    const [startTime, setStartTime] = useState('');
    const [delayTime, setDelayTime] = useState(0);
    const [prewarningTime, setPrewarningTime] = useState(0);
    const [message, setMessage] = useState('');
    const [scriptsData, setScriptsData] = useState([]);
    const [scriptId, setScriptId] = useState('');
    const [eventOptions, setEventOption] = useState([]);

    useEffect(() => {
        getScriptsData();
        getSchemeType();
    }, []);

    const getScriptsData = () => {
        ApiHelper.get('/api/server/scripts/')
            .then((res) => {
                setScriptsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const getSchemeType = () => {
        ApiHelper.get('/api/management/scheme_type/').then((res) => {
            const data = res.data.filter((schemeType) => schemeType.schemetypeID >= 2);
            setEventOption(data);
        });
    };

    const handleChangeDays = (e) => {
        setDays({
            ...days,
            [e.target.name]: e.target.checked
        });
    };

    const handleSubmit = () => {
        if (
            days.sunday === false &&
            days.monday === false &&
            days.tuesday === false &&
            days.wednesday === false &&
            days.thursday === false &&
            days.friday === false &&
            days.saturday === false
        ) {
            toast.error('Please select the day');
            return true;
        }
        if (startTime === '') {
            toast.error('Please select the start time');
            return true;
        }
        if (prewarningTime === '') {
            toast.error('Please fill the pre-warning time');
            return true;
        }
        if (validateInputValue(prewarningTime, 'minutes') === false) {
            toast.error('Pre-warning time should be less than 60');
            return true;
        }
        if (validateInputValue(delayTime, 'hours') === false) {
            toast.error('Delay time should be less than 24');
            return true;
        }
        if (eventType === 'script' && scriptId === '') {
            toast.error('Please select the script');
            return true;
        }
        const newScheduledEventData = {
            days,
            eventType,
            startTime,
            delayTime,
            prewarningTime,
            script: eventType === 'script' ? scriptId : '',
            message
        };
        if (localStorage.getItem('policyScheduledEventsData')) {
            const scheduledEventsData = JSON.parse(localStorage.getItem('policyScheduledEventsData'));
            const newData = JSON.stringify([...scheduledEventsData, newScheduledEventData]);
            localStorage.setItem('policyScheduledEventsData', newData);
        } else {
            const newData = JSON.stringify([newScheduledEventData]);
            localStorage.setItem('policyScheduledEventsData', newData);
        }
        onClose();
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Add New Scheduled Event</DialogTitle>
            <DialogContent sx={{ maxWidth: '600px' }}>
                <Box sx={{ mt: 2 }}>
                    <FormGroup aria-label="position" row>
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="sunday" checked={days.sunday} onChange={handleChangeDays} />}
                            label="Sun"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="monday" checked={days.monday} onChange={handleChangeDays} />}
                            label="Mon"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="tuesday" checked={days.tuesday} onChange={handleChangeDays} />}
                            label="Tue"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="wednesday" checked={days.wednesday} onChange={handleChangeDays} />}
                            label="Wed"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="thursday" checked={days.thursday} onChange={handleChangeDays} />}
                            label="Thr"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="friday" checked={days.friday} onChange={handleChangeDays} />}
                            label="Fri"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="saturday" checked={days.saturday} onChange={handleChangeDays} />}
                            label="Sat"
                            labelPlacement="top"
                        />
                    </FormGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Event Selector</InputLabel>
                                <Select label="Event Selector" value={eventType} onChange={(e) => setEventType(e.target.value)}>
                                    {eventOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.name}>
                                            {capitalizeFirstLetter(data.name)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>Start Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="prewarningTime">Pre-warning Time</InputLabel>
                                <OutlinedInput
                                    id="prewarningTime"
                                    type="number"
                                    label="Pre-warning Time"
                                    value={prewarningTime}
                                    onChange={(e) => setPrewarningTime(e.target.value)}
                                    endAdornment={<InputAdornment position="end">min(s)</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="delayTime">Delay Time</InputLabel>
                                <OutlinedInput
                                    id="delayTime"
                                    type="number"
                                    label="Delay Time"
                                    value={delayTime}
                                    onChange={(e) => setDelayTime(e.target.value)}
                                    endAdornment={<InputAdornment position="end">hour(s)</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                sx={{ width: '100%' }}
                                label={'Message'}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                        </Grid>
                        {eventType === 'script' && (
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select script</InputLabel>
                                    <Select label="Select script" value={scriptId} onChange={(e) => setScriptId(e.target.value)}>
                                        {scriptsData.map((data, key) => (
                                            <MenuItem key={key} value={data.id}>
                                                {data.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

AddScheduledEventDialog.propTypes = {
    onClose: PropTypes.func,
    scriptStatus: PropTypes.bool
};

export default AddScheduledEventDialog;
